<template>
    <div>
        <v-card :loading="loading" class="mx-auto" outlined>
            <template v-slot:progress>
                <v-progress-linear
                    :color="$globalSettings.progressbarClasses"
                    height="10"
                    indeterminate
                ></v-progress-linear>
            </template>

            <v-card-title>
                <v-row>
                    <v-col cols="12" sm="12">
                        <div v-if="reportType">
                            <span class="secondary-text">{{ reportConfigs[reportType].title }}</span>
                            <button type="button" class="btn btn-sm btn-outline-secondary float-right mr-5" v-on:click="exportToXLSX(reportType)" :disabled="loading"><i class="fas fa-file-excel"></i> Export Excel</button>
                            <button v-if="reportType !== 'validity'" type="button" class="btn btn-sm btn-outline-secondary float-right mr-5" v-on:click="exportPDF(reportType)" :disabled="loading"><i class="fas fa-file-pdf"></i> Export PDF</button>
                        </div>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
                <v-row v-if="reportType">
                    <v-col v-for="filter in reportConfigs[reportType].filters" :key="filter.model" :cols="filter.cols || 12" :sm="filter.sm || 3">
                        <template v-if="filter.type === 'date'">
                            <v-menu v-model="calendarOpen[filter.model]" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field dense outlined :value="reports[reportType][filter.model] && $globalHelpers.getFormattedDate(reports[reportType][filter.model], '', 'MMMM D, YYYY')" :label="filter.label" readonly v-bind="attrs" v-on="on" clearable @click:clear="reports[reportType][filter.model] = null" prepend-icon="fa-regular fa-calendar" :disabled="loading"></v-text-field>
                                </template>
                                <v-date-picker v-model="reports[reportType][filter.model]">
                                    <v-btn text color="primary" @click="calendarOpen[filter.model] = false">Close</v-btn>
                                </v-date-picker>
                            </v-menu>
                        </template>
                        <template v-else-if="filter.type === 'select'">
                            <v-select dense outlined :items="getItems(filter.items)" :item-value="filter.itemValue || 'id'" :item-text="filter.itemText || 'label'" :label="filter.label" v-model="reports[reportType][filter.model]" :multiple="filter.multiple" small-chips :disabled="loading" @change="callFilterChange(filter)">
                                <template v-if="filter.multiple" v-slot:prepend-item>
                                    <v-list-item ripple @mousedown.prevent @click="toggleSelection(filter.model, filter.items)">
                                        <v-icon :color="reports[reportType][filter.model].length > 0 ? 'indigo darken-4' : ''">fa-regular fa-square-check</v-icon>
                                        <v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Select All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item-action>
                                    </v-list-item>
                                </template>
                                <template v-if="filter.multiple" v-slot:selection="{ item, index }">
                                    <v-chip v-if="index === 0" small><span>{{ item[filter.itemText || 'label'] }}</span></v-chip>
                                    <span v-if="index === 1" class="grey--text text-caption">(+{{ reports[reportType][filter.model].length - 1 }} others)</span>
                                </template>
                            </v-select>
                        </template>
                    </v-col>
                    <v-col>
                        <v-btn class="float-end" color="primary" @click="generate('reports.' + reportType)" :disabled="getGenerateDisabled()">Generate</v-btn>
                    </v-col>
                </v-row>

                <div id="debugReport"></div>

                <v-row v-if="reportType && showReport">
                    <v-col cols="12" sm="12">
                        <table class="report_table" :id="'reports.' + reportType">
                            <tbody>
                            <tr>
                                <td v-for="col in reportConfigs[reportType].columns" :key="col.label" class="text-left font-bold" :style="col.headerStyle">{{ col.label }}</td>
                            </tr>
                            <tr v-for="(item, i) in reports[reportType].list" :key="i">
                                <td v-for="col in reportConfigs[reportType].columns" :key="col.label" :class="col.class || 'text-left'">
                                    {{ typeof col.value === 'function' ? col.value.call(this, item, i) : getNestedValue(item, col.value) }}
                                </td>
                            </tr>
                            <tr v-if="reportConfigs[reportType].totalRow">
                                <td v-for="cell in reportConfigs[reportType].totalRow" :key="cell.index" :colspan="cell.colspan" :class="cell.class">
                                    {{ cell.value ? (typeof cell.value === 'string' && cell.value in reports[reportType] ? reports[reportType][cell.value] : cell.value) : '' }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </v-col>
                </v-row>

            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import Vue from "vue";
import XLSX from "xlsx";
import html2pdf from "html2pdf.js";

export default {
    name: "Reports_view",
    components: {},
    data: () => ({
        loading: false,
        routeParams: null,

        calendarOpen: {
            startDate: false,
            endDate: false,
        },
        showReport: false,
        reportType: null,

        applicationTypes: [],
        statuses: [],
        serviceOffices: [],
        roles: [],
        exporterTypes: [],
        designations: [],
        factoryTypes: [],
        validTillYears: [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032],

        reports:{
            applications: {
                reportName: 'Applications Report',
                startDate: null,
                endDate: null,
                selectedApplicationTypeIds: [],
                selectedStatusIds: [],
                selectedServiceOfficeIds: [],
                selectedExporterTypeId: null,
                selectedFactoryTypeId: null,
                selectedFactoryDistrictIds: [],
                list: [],
                count: 0,
                filteredExporterTypes: [],
                filteredFactoryTypes: [],
                filteredServiceOffices: [],
                factoryDistricts: [],
            },
            payments: {
                reportName: 'Payments Report',
                startDate: null,
                endDate: null,
                selectedServiceOfficeIds: [],
                selectedExporterTypeIds: [],
                list: [],
                count: 0,
                totalAmount: 0,
                filteredExporterTypes: [],
                filteredServiceOffices: [],
            },
            users: {
                reportName: 'Users Report',
                type: null,
                selectedServiceOfficeIds: [],
                selectedRoleIds: [],
                selectedExporterTypeIds: [],
                selectedDesignationIds: [],
                selectedFactoryDistrictIds: [],
                list: [],
                count: 0,
                roles: [],
                filteredExporterTypes: [],
                filteredDesignations: [],
                filteredUserTypes: [
                    {id: 0, label: 'EPB Users'},
                    {id: 2, label: 'Exporter Users'},
                ],
                filteredServiceOffices: [],
                factoryDistricts: [],
            },
            validity: {
                reportName: 'Exporters Validity Report',
                selectedServiceOfficeIds: [],
                selectedExporterTypeIds: [],
                selectedValidTillYears: [],
                list: [],
                count: 0,
                roles: [],
                filteredExporterTypes: [],
                filteredServiceOffices: [],
            },
            inspections: {
                reportName: 'Inspections Report',
                startDate: null,
                endDate: null,
                selectedApplicationTypeIds: [],
                selectedServiceOfficeIds: [],
                selectedExporterTypeIds: [],
                list: [],
                count: 0,
                filteredExporterTypes: [],
                filteredServiceOffices: [],
            },
            inspectionCounts: {
                reportName: 'Inspection Counts Report',
                startDate: null,
                endDate: null,
                selectedServiceOfficeIds: [],
                selectedRoleIds: [],
                selectedExporterTypeIds: [],
                selectedDesignationIds: [],
                list: [],
                count: 0,
                roles: [],
                filteredExporterTypes: [],
                filteredDesignations: [],
                filteredServiceOffices: [],
            },
        },

        reportMap: {
            'applications': {
                elementId: 'reports.applications',
                filenamePDF: 'Reg_Ren_Applications_Report.pdf',
                filenameExcel: 'Reg_Ren_Applications_Report.xlsx',
                title: '1. Reg./Renewal Applications',
                reportKey: 'applications',
                endpoint: 'report.applications'
            },
            'payments': {
                elementId: 'reports.payments',
                filenamePDF: 'Reg_Ren_Payments_Report.pdf',
                filenameExcel: 'Reg_Ren_Payments_Report.xlsx',
                title: '2. Reg./Renewal Payments',
                reportKey: 'payments',
                endpoint: 'report.ea.payments'
            },
            'users': {
                elementId: 'reports.users',
                filenamePDF: 'EMS_Users_Report.pdf',
                filenameExcel: 'EMS_Users_Report.xlsx',
                title: '3. EMS Users',
                reportKey: 'users',
                endpoint: 'report.users'
            },
            'validity': {
                elementId: 'reports.validity',
                filenamePDF: 'EMS_Validity_Report.pdf',
                filenameExcel: 'EMS_Validity_Report.xlsx',
                title: '4. EMS Exporters Validity',
                reportKey: 'validity',
                endpoint: 'report.validity'
            },
            'inspections': {
                elementId: 'reports.inspections',
                filenamePDF: 'Inspections_Report.pdf',
                filenameExcel: 'Inspections_Report.xlsx',
                title: '5. Inspections Report',
                reportKey: 'inspections',
                endpoint: 'report.inspections'
            },
            'inspectionCounts': {
                elementId: 'reports.inspectionCounts',
                filenamePDF: 'Inspection_Counts_Report.pdf',
                filenameExcel: 'Inspection_Counts_Report.xlsx',
                title: 'Inspection Counts Report',
                reportKey: 'inspectionCounts',
                endpoint: 'report.inspectionCounts' // use dotted endpoint to match existing pattern
            },
        },
    }),
    computed: {
        reportConfigs() {
            return {
                applications: {
                    title: '1. Applications Report',
                    endpoint: 'report.applications',
                    filters: [
                        { model: 'startDate', label: 'From', type: 'date', sm: 3 },
                        { model: 'endDate', label: 'To', type: 'date', sm: 3 },
                        { model: 'selectedApplicationTypeIds', label: 'Application Type', type: 'select', items: 'applicationTypes', multiple: true, sm: 2 },
                        { model: 'selectedStatusIds', label: 'Application Status', type: 'select', items: 'statuses', multiple: true, sm: 2 },
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.applications.filteredServiceOffices', multiple: true, sm: 2 },
                        { model: 'selectedExporterTypeId', label: 'Department', type: 'select', items: 'reports.applications.filteredExporterTypes', sm: 2, change: 'reportApplicationsModifyFilters' },
                        { model: 'selectedFactoryTypeId', label: 'Factory Type', type: 'select', items: 'reports.applications.filteredFactoryTypes', sm: 2, itemValue: 'Id', itemText: 'Label' },
                        { model: 'selectedFactoryDistrictIds', label: 'Factory District', type: 'select', items: 'reports.applications.factoryDistricts', sm: 2, multiple: true },
                    ],
                    columns: [
                        { label: 'SL#', value: (item, i) => i + 1 },
                        { label: 'App. ID', value: 'Id' },
                        { label: 'Organization', value: 'User.OrganizationName' },
                        { label: 'Reg. No.', value: 'User.Username' },
                        { label: 'Type', value: (item) => {
                            const type = this.$globalHelpers.humanize(this.applicationTypes.find(x => x.id === item.ApplicationType).label);
                            return type === 'Address Change' ? 'Info. Change' : type;
                        } },
                        { label: 'Status', value: (item) => this.$globalHelpers.humanize(this.statuses.find(x => x.id === item.StatusId).label) },
                        { label: 'Factory Type', value: (item) => item.EAInspections.length > 0 ? item.EAInspections[0].EAInspectionFactoryTypes.map(ea => ea.FactoryType.Label).join(', ') : '' },
                        { label: 'Email', value: 'User.Email' },
                        { label: 'Office. Phone', value: 'OfficePhone' },
                        { label: 'Factory Address', value: 'FactoryAddress' },
                        { label: 'Factory District', value: (item) => item.FactoryDistrictId ? this.$globalHelpers.getDistrictName(item.FactoryDistrictId) : '' },
                        { label: 'Factory Thana', value: (item) => item.FactoryThanaId ? this.$globalHelpers.getThanaName(item.FactoryThanaId) : '' },
                        { label: 'Service Office', value: (item) => this.$globalHelpers.humanize(this.serviceOffices.find(x => x.id === item.ServiceOfficeId).label) },
                        { label: 'Valid Till', value: (item) => item.ApplicationType !== this.$globalEnums.ApplicationTypes.Address_Change && item.StatusId === this.$globalEnums.ApplicationStatuses.Approved ? this.$globalHelpers.getFormattedDate(item.ValidTillDate, '', 'DD/MM/YYYY') : '' },
                        { label: 'Updated On', value: (item) => this.$globalHelpers.getFormattedDate(item.Modified, '', 'DD/MM/YYYY') },
                    ],
                    totalRow: [
                        {},
                        { value: 'Total:' },
                        { value: 'count' },
                        { colspan: 12 },
                    ],
                },
                payments: {
                    title: '2. Payments Report',
                    endpoint: 'report.payments',
                    filters: [
                        { model: 'startDate', label: 'From', type: 'date', sm: 3 },
                        { model: 'endDate', label: 'To', type: 'date', sm: 3 },
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.payments.filteredServiceOffices', multiple: true, sm: 3 },
                        { model: 'selectedExporterTypeIds', label: 'Department', type: 'select', items: 'reports.payments.filteredExporterTypes', multiple: true, sm: 3 },
                    ],
                    columns: [
                        { label: 'Payment Method', value: (item) => item.PaymentType === 'spg'? 'Sonali Bank': 'Payslip' },
                        { label: 'Count', value: 'Count', class: 'text-right' },
                        { label: 'Amount', value: 'Amount', class: 'text-right' },
                    ],
                    totalRow: [
                        { value: 'Total:', class: 'font-bold' },
                        { value: 'count', class: 'text-right' },
                        { value: 'totalAmount', class: 'text-right' },
                    ],
                },
                users: {
                    title: '3. Users Report',
                    endpoint: 'report.users',
                    filters: [
                        { model: 'type', label: 'Type', type: 'select', items: 'reports.users.filteredUserTypes', sm: 2, change: 'reportUsersModifyFilters' },
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.users.filteredServiceOffices', multiple: true, sm: 2 },
                        { model: 'selectedRoleIds', label: 'Role', type: 'select', items: 'roles', multiple: true, sm: 2 },
                        { model: 'selectedExporterTypeIds', label: 'Department', type: 'select', items: 'reports.users.filteredExporterTypes', multiple: true, sm: 2 },
                        { model: 'selectedDesignationIds', label: 'Designation', type: 'select', items: 'reports.users.filteredDesignations', multiple: true, sm: 2 },
                    ],
                    columns: [
                        { label: 'SL#', value: (item, i) => i + 1 },
                        { label: 'Email', value: 'Email' },
                        { label: 'Name', value: 'Name' },
                        { label: 'Designation', value: (item) => this.$globalHelpers.humanize(this.designations.find(x => x.id === item.DesignationId).label) },
                        { label: 'Organization', value: 'OrganizationName' },
                        { label: 'Username', value: 'Username' },
                        { label: 'Mobile', value: 'Mobile' },
                        { label: 'Department', value: (item) => this.$globalHelpers.humanize(this.exporterTypes.find(x => x.id === item.ExporterType).label) },
                        { label: 'Role', value: (item) => this.$globalHelpers.humanize(this.roles.find(x => x.id === item.RoleId).label) },
                        { label: 'Service Office', value: (item) => this.$globalHelpers.humanize(this.serviceOffices.find(x => x.id === item.ServiceOfficeId).label) },
                        { label: 'Updated', value: (item) => this.$globalHelpers.getFormattedDate(item.Modified, '', 'DD/MM/YYYY') },
                    ],
                    totalRow: [
                        {},
                        { value: 'Total:' },
                        { value: 'count' },
                        { colspan: 8 },
                    ],
                },
                validity: {
                    title: '4. Exporter Validity Report',
                    endpoint: 'report.validity',
                    filters: [
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.validity.filteredServiceOffices', multiple: true, sm: 3 },
                        { model: 'selectedExporterTypeIds', label: 'Department', type: 'select', items: 'reports.validity.filteredExporterTypes', multiple: true, sm: 3 },
                        { model: 'selectedValidTillYears', label: 'Valid Till Year', type: 'select', items: 'validTillYears', multiple: true, sm: 3 },
                    ],
                    columns: [
                        { label: 'SL#', value: (item, i) => i + 1 },
                        { label: 'Exporter Name', value: 'OrganizationName' },
                        { label: 'Reg. No.', value: 'Username' },
                        { label: 'Service Office', value: (item) => this.$globalHelpers.humanize(this.serviceOffices.find(x => x.id === item.ServiceOfficeId).label) },
                        { label: 'Department', value: (item) => this.$globalHelpers.humanize(this.exporterTypes.find(x => x.id === item.ExporterType).label) },
                        { label: 'Valid Till', value: (item) => item.ExporterValidTillDate? this.$globalHelpers.getFormattedDate(item.ExporterValidTillDate, '', 'DD/MM/YYYY') :'' },
                    ],
                    totalRow: [
                        { colspan: 4 },
                        { value: 'Total:' },
                        { value: 'count' },
                    ],
                },
                inspections: {
                    title: 'Inspections Report',
                    endpoint: 'report.inspections',
                    filters: [
                        { model: 'startDate', label: 'From', type: 'date', sm: 3 },
                        { model: 'endDate', label: 'To', type: 'date', sm: 3 },
                        { model: 'selectedApplicationTypeIds', label: 'Application Type', type: 'select', items: 'applicationTypes', multiple: true, sm: 2 },
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.inspections.filteredServiceOffices', multiple: true, sm: 2 },
                        { model: 'selectedExporterTypeIds', label: 'Department', type: 'select', items: 'reports.inspections.filteredExporterTypes', multiple: true, sm: 2 },
                    ],
                    columns: [
                        { label: 'SL#', value: (item, i) => i + 1 },
                        { label: 'App. ID', value: 'EAId' },
                        { label: 'Company Name', value: 'EA.User.OrganizationName' },
                        { label: 'Application Type', value: (item) => {
                                const type = this.$globalHelpers.humanize(this.applicationTypes.find(x => x.id === item.EA.ApplicationType).label);
                                return type === 'Address Change' ? 'Info. Change' : type;
                            } },
                        { label: 'Department', value: (item) => this.$globalHelpers.humanize(this.exporterTypes.find(x => x.id === item.EA.User.ExporterType).label) },
                        { label: 'Reg. No.', value: 'EA.User.Username' },
                        { label: 'Inspectors', value: (item) => item.EAInspectors.slice().sort((a, b) => b.isLeader - a.isLeader).map(inspector => `${inspector.IPName} (${this.$globalHelpers.humanize(this.designations.find(x => x.id === inspector.IPDesignationId).label)}, ${this.$globalHelpers.humanize(this.serviceOffices.find(x => x.id === inspector.IPServiceOfficeId).label)})`).join(', ') },
                        { label: 'Application Date', value: (item) => {
                            const log = item.EA.EAStatusLogs.find(log => log.StatusId === this.$globalEnums.ApplicationStatuses.Director_desk);
                            return log ? this.$globalHelpers.getFormattedDate(log.Created, '', 'DD/MM/YYYY') : '';
                        } },
                        { label: 'Team Approval Date', value: (item) => {
                            const log = item.EA.EAStatusLogs.find(log => log.StatusId === this.$globalEnums.ApplicationStatuses.Team_Approval);
                            return log ? this.$globalHelpers.getFormattedDate(log.Created, '', 'DD/MM/YYYY') : '';
                        } },
                        { label: 'Report Submit Date', value: (item) => {
                            const log = item.EA.EAStatusLogs.find(log => log.StatusId === this.$globalEnums.ApplicationStatuses.Report_Submitted);
                            return log ? this.$globalHelpers.getFormattedDate(log.Created, '', 'DD/MM/YYYY') : '';
                        } },
                    ],
                    totalRow: [
                        { colspan: 8 },
                        { value: 'Total:'},
                        { value: 'count' },
                    ],
                },
                inspectionCounts: {
                    title: 'Inspection Counts Report',
                    endpoint: 'report.inspectionCounts', // endpoint name provided by user (dotted to match config)
                    filters: [
                        { model: 'startDate', label: 'From', type: 'date', sm: 3 },
                        { model: 'endDate', label: 'To', type: 'date', sm: 3 },
                        { model: 'selectedServiceOfficeIds', label: 'Service Office', type: 'select', items: 'reports.inspectionCounts.filteredServiceOffices', multiple: true, sm: 2 },
                        { model: 'selectedExporterTypeIds', label: 'Department', type: 'select', items: 'reports.inspectionCounts.filteredExporterTypes', multiple: true, sm: 2 },
                        { model: 'selectedDesignationIds', label: 'Designation', type: 'select', items: 'reports.inspectionCounts.filteredDesignations', multiple: true, sm: 2 },
                    ],
                    columns: [
                        { label: 'SL#', value: (item, i) => i + 1 },
                        { label: 'Name', value: 'Name' },
                        { label: 'Designation', value: (item) => this.$globalHelpers.humanize(this.designations.find(x => x.id === item.DesignationId).label) },
                        { label: 'Department', value: (item) => this.$globalHelpers.humanize(this.exporterTypes.find(x => x.id === item.ExporterType).label) },
                        { label: 'Service Office', value: (item) => this.$globalHelpers.humanize(this.serviceOffices.find(x => x.id === item.ServiceOfficeId).label) },
                        { label: 'Email', value: 'Email' },
                        { label: 'Mobile', value: 'Mobile' },
                        { label: 'Inspection Count', value: 'InspectionCount' },
                        { label: 'Latest Insp. Date', value: (item) => item.LastInspectionDate? this.$globalHelpers.getFormattedDate(item.LastInspectionDate, '', 'DD/MM/YYYY'): '' },
                    ],
                    totalRow: [
                    ],
                },
            };
        },
    },
    methods:{
        callFilterChange: function(filter) {
            if (filter.change) {
                this[filter.change]();
            }
        },
        toggleSelection: function(model, itemsKey) {
            const items = this.getItems(itemsKey);
            const selected = this.reports[this.reportType][model];
            const allIds = items.map(item => item.id !== undefined ? item.id : item);
            if (selected.length === allIds.length) {
                this.reports[this.reportType][model] = [];
            } else {
                this.reports[this.reportType][model] = allIds;
            }
        },
        reportApplicationsModifyFilters: function (){
            console.log('modify');
            this.reports.applications.filteredFactoryTypes = [];
            this.reports.applications.filteredFactoryTypes = this.factoryTypes.filter(x => x.Type === this.reports.applications.selectedExporterTypeId);
        },
        reportUsersModifyFilters: function (){
            console.log('modify');
            this.reports.users.selectedRoleIds = [];
            this.reports.users.selectedExporterTypeIds = [];
            this.reports.users.selectedDesignationIds = [];
            this.reports.users.selectedFactoryDistrictIds = [];
            const districtFilterIdx = this.reportConfigs.users.filters.findIndex(f => f.model === 'selectedFactoryDistrictIds');
            const exporterColIdx = this.reportConfigs.users.columns.findIndex(c => c.label === 'Factory Address');
            if(this.reports.users.type === 2){
                this.reports.users.roles = this.roles.filter(x => x.id === this.$globalEnums.roles.Exporter);
                this.reports.users.filteredExporterTypes = this.$globalHelpers.enumToArray(this.$globalEnums.ExporterTypes).filter(x => x.id === 1 || x.id === 2);
                this.reports.users.filteredDesignations = this.$globalHelpers.enumToArray(this.$globalEnums.Designations).filter(x => x.id >= 100);
                if (districtFilterIdx === -1) {
                    this.reportConfigs.users.filters.push({ model: 'selectedFactoryDistrictIds', label: 'Factory District', type: 'select', items: 'reports.users.factoryDistricts', sm: 2, multiple: true });
                }
                if (exporterColIdx === -1) {
                    const insertAt = this.reportConfigs.users.columns.findIndex(c => c.label === 'Updated');
                    this.reportConfigs.users.columns.splice(insertAt, 0,
                        { label: 'Factory Address', value: 'FactoryAddress' },
                        { label: 'Factory District', value: (item) => item.FactoryDistrictId ? this.$globalHelpers.getDistrictName(item.FactoryDistrictId) : '' },
                        { label: 'Factory Thana', value: (item) => item.FactoryThanaId ? this.$globalHelpers.getThanaName(item.FactoryThanaId) : '' },
                        { label: 'Valid Till', value: (item) => item.ExporterValidTillDate ? this.$globalHelpers.getFormattedDate(item.ExporterValidTillDate, '', 'DD/MM/YYYY') : '' },
                    );
                    this.reportConfigs.users.totalRow[3].colspan = 12;
                }
            }else{
                this.reports.users.roles = this.roles.filter(x => x.id !== this.$globalEnums.roles.Exporter);
                this.reports.users.filteredExporterTypes = this.exporterTypes;
                this.reports.users.filteredDesignations = this.$globalHelpers.enumToArray(this.$globalEnums.Designations).filter(x => x.id !== null && x.id <= 50);
                if (districtFilterIdx !== -1) {
                    this.reportConfigs.users.filters.splice(districtFilterIdx, 1);
                }
                if (exporterColIdx !== -1) {
                    this.reportConfigs.users.columns.splice(exporterColIdx, 4);
                    this.reportConfigs.users.totalRow[3].colspan = 8;
                }
            }
        },
        loadFactoryTypes:  async function () {
            this.loading = true;
            await this.$axios.post(this.$globalSettings.api.endpoints.factoryType.list, {
                Label: null,
                Type: null,
                IsAscendingOrder: true,
                Skip: null,
                Take: null,
            })
                .then((response) => {
                    this.factoryTypes = response.data.List;
                    this.factoryTypes.unshift({Id: null, Label: 'All', Type: 1}, {Id: null, Label: 'All', Type: 2});
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.$globalHelpers.processHttpErrorsToast(error);
                })
        },
        generate: async function (report_key) {
            const type = report_key.split('.')[1];
            const config = this.reportConfigs[type];
            this.showReport = true;
            this.loading = true;
            this.reports[type].list = [];
            await this.$axios.post(this.$globalSettings.api.endpoints[config.endpoint.split('.')[0]][config.endpoint.split('.')[1]], this.reports[type]).then(async (response) => {
                this.loading = false;
                if (response.data.Status === 'success') {
                    this.reports[type].list = response.data.List;
                    if (response.data.Count !== undefined) this.reports[type].count = response.data.Count;
                    if (response.data.TotalAmount !== undefined) this.reports[type].totalAmount = response.data.TotalAmount;
                    Vue.$toast.open({message: "Report Generated.", type: response.data.Status});
                }else{
                    Vue.$toast.open({message: response.data.Msg, type: response.data.Status});
                }
            }).catch((error) => {
                this.loading = false;
                this.reports[type].list = [];
                this.$globalHelpers.processHttpErrorsToast(error, true);
            });
        },
        exportToXLSX: function (report) {
            const config = this.reportMap[report];
            if (!config) {
                return;
            }
            const originalTable = document.getElementById(config.elementId);
            if (!originalTable) {
                return;
            }

            // Clone the table to avoid modifying the original in the DOM
            const clonedTable = originalTable.cloneNode(true);

            // Get the column count from the first row
            const firstRow = clonedTable.querySelector('tr');
            const columnCount = firstRow ? firstRow.querySelectorAll('td, th').length : 1;

            // Get report-specific data
            const reportData = this.reports[config.reportKey];
            //console.log(reportData);
            const fromDate = this.$globalHelpers.getFormattedDate(reportData.startDate, '', 'DD/MM/YYYY');
            const toDate = this.$globalHelpers.getFormattedDate(reportData.endDate, '', 'DD/MM/YYYY');

            let applicationTypeText = 'All';
            let userTypeText;
            let serviceOfficeText = 'All';
            let exporterTypeText = 'All';
            let exporterTypesText = 'All';
            let factoryTypeText = 'All';
            let statusText = 'All';
            let rolesText = 'All';
            let designationText = 'All';
            let yearsText = 'All';

            if (Array.isArray(reportData.selectedApplicationTypeIds) && reportData.selectedApplicationTypeIds.length > 0 && reportData.selectedApplicationTypeIds.length < this.applicationTypes.length) {
                applicationTypeText = reportData.selectedApplicationTypeIds.map(id => {
                    const applicationType = this.applicationTypes.find(s => s.id === id);
                    return this.$globalHelpers.humanize(applicationType?.label || '');
                }).join(', ');
            }

            if(this.reports.users.type === 2){
                userTypeText = 'Exporter';
            }else{
                userTypeText = 'EPB Users';
            }

            if (Array.isArray(reportData.selectedServiceOfficeIds) && reportData.selectedServiceOfficeIds.length > 0 && reportData.selectedServiceOfficeIds.length < this.serviceOffices.length) {
                serviceOfficeText = reportData.selectedServiceOfficeIds.map(id => {
                    const serviceOffice = this.serviceOffices.find(s => s.id === id);
                    return this.$globalHelpers.humanize(serviceOffice?.label || '');
                }).join(', ');
            }
            if (reportData.selectedExporterTypeId) {
                exporterTypeText = reportData.filteredExporterTypes.find(x => x.id === reportData.selectedExporterTypeId).label;
            }
            if (reportData.selectedFactoryTypeId) {
                factoryTypeText = reportData.filteredFactoryTypes.find(x => x.Id === reportData.selectedFactoryTypeId).Label;
            }
            if (Array.isArray(reportData.selectedExporterTypeIds) && reportData.selectedExporterTypeIds.length > 0 && reportData.selectedExporterTypeIds.length < this.exporterTypes.length) {
                exporterTypesText = reportData.selectedExporterTypeIds.map(id => {
                    const exporterType = this.exporterTypes.find(s => s.id === id);
                    return this.$globalHelpers.humanize(exporterType?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedStatusIds) && reportData.selectedStatusIds.length > 0 && reportData.selectedStatusIds.length < this.statuses.length) {
                statusText = reportData.selectedStatusIds.map(id => {
                    const status = this.statuses.find(s => s.id === id);
                    return this.$globalHelpers.humanize(status?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedRoleIds) && reportData.selectedRoleIds.length > 0 && reportData.selectedRoleIds.length < this.roles.length) {
                rolesText = reportData.selectedRoleIds.map(id => {
                    const role = this.roles.find(s => s.id === id);
                    return this.$globalHelpers.humanize(role?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedDesignationIds) && reportData.selectedDesignationIds.length > 0 && reportData.selectedDesignationIds.length < this.designations.length) {
                designationText = reportData.selectedDesignationIds.map(id => {
                    const designation = this.designations.find(s => s.id === id);
                    return this.$globalHelpers.humanize(designation?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedValidTillYears) && reportData.selectedValidTillYears.length > 0 && reportData.selectedValidTillYears.length < this.validTillYears.length) {
                yearsText = reportData.selectedValidTillYears.join(', ');
            }

            // Create header rows
            let headerHTML = `
                <tr><td colspan="${columnCount}" style="text-align: center;">Export Promotion Bureau</td></tr>
                <tr><td colspan="${columnCount}" style="text-align: center;">Report: &nbsp; <strong>${config.title}</strong></td></tr>`;
            if(config.reportKey !== 'validity' && config.reportKey !== 'users'){
                headerHTML += `
                    <tr><td colspan="${columnCount}" style="text-align: center;">From: ${fromDate} To: ${toDate}</td></tr>`;
            }

            headerHTML += `
                <tr>
                    <td colspan="${columnCount}" style="text-align: center;">
                        ${config.reportKey === 'applications'? 'Type: ' + applicationTypeText + ', Department: ' + exporterTypeText + ', Factory Type: ' + factoryTypeText  + ', Service Office: ' + serviceOfficeText + ', Statuses: ' + statusText : ''}
                        ${config.reportKey === 'payments'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText: ''}
                        ${config.reportKey === 'users'? 'Type: ' + userTypeText + ', Department: ' + exporterTypesText + ', Service Office: ' + serviceOfficeText + ', Roles: ' + rolesText + ', Designations: ' + designationText : ''}
                        ${config.reportKey === 'validity'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText + ', Year: ' + yearsText : ''}
                        ${config.reportKey === 'inspections'? 'Application Type: ' + applicationTypeText + ', Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText : ''}
                        ${config.reportKey === 'inspectionCounts'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText + ', Designations: ' + designationText : ''}
                    </td>
                </tr>
                <tr><td colspan="${columnCount}"></td></tr>
            `;

            // Create a temporary tbody and insert at the top
            const headerWrapper = document.createElement('tbody');
            headerWrapper.innerHTML = headerHTML;

            const tableBody = clonedTable.querySelector('tbody');
            if (tableBody) {
                // Prepend each header row to the top of tbody
                Array.from(headerWrapper.children).reverse().forEach(row => {
                    tableBody.insertBefore(row, tableBody.firstChild);
                });
            } else {
                // Fallback: prepend to table if <tbody> is not present
                Array.from(headerWrapper.children).reverse().forEach(row => {
                    clonedTable.insertBefore(row, clonedTable.firstChild);
                });
            }

            // Export
            const wb = XLSX.utils.table_to_book(clonedTable, { sheet: "Sheet1" });
            XLSX.writeFile(wb, config.filenameExcel);
        },
        exportPDF: function (report) {
            this.loading = true;
            const config = this.reportMap[report];
            if (!config) {
                this.loading = false;
                return;
            }

            const element = document.getElementById(config.elementId);
            if (!element) {
                this.loading = false;
                return;
            }

            const originalRows = Array.from(element.querySelectorAll('tbody > tr'));
            const headerRow = originalRows[0] || null; // Column titles (may be null)

            // Determine footer rows: rely on the report config's totalRow to know if there's a footer
            let footerRows = [];
            if (this.reportConfigs && this.reportConfigs[config.reportKey] && this.reportConfigs[config.reportKey].totalRow) {
                // If a footer exists, it's expected to be the last row
                if (originalRows.length > 1) {
                    footerRows = [originalRows[originalRows.length - 1]];
                }
            }

            // Data rows are everything between the header and the footer (if present)
            const dataRows = originalRows.slice(1, originalRows.length - footerRows.length);

            const reportData = this.reports[config.reportKey];
            const fromDate = this.$globalHelpers.getFormattedDate(reportData.startDate, '', 'DD/MM/YYYY');
            const toDate = this.$globalHelpers.getFormattedDate(reportData.endDate, '', 'DD/MM/YYYY');

            let applicationTypeText = 'All';
            let userTypeText;
            let serviceOfficeText = 'All';
            let exporterTypeText = 'All';
            let exporterTypesText = 'All';
            let factoryTypeText = 'All';
            let statusText = 'All';
            let rolesText = 'All';
            let designationText = 'All';
            let yearsText = 'All';

            if (Array.isArray(reportData.selectedApplicationTypeIds) && reportData.selectedApplicationTypeIds.length > 0 && reportData.selectedApplicationTypeIds.length < this.applicationTypes.length) {
                applicationTypeText = reportData.selectedApplicationTypeIds.map(id => {
                    const applicationType = this.applicationTypes.find(s => s.id === id);
                    return this.$globalHelpers.humanize(applicationType?.label || '');
                }).join(', ');
            }

            if(this.reports.users.type === 2){
                userTypeText = 'Exporter';
            }else{
                userTypeText = 'EPB Users';
            }

            if (Array.isArray(reportData.selectedServiceOfficeIds) && reportData.selectedServiceOfficeIds.length > 0 && reportData.selectedServiceOfficeIds.length < this.serviceOffices.length) {
                serviceOfficeText = reportData.selectedServiceOfficeIds.map(id => {
                    const serviceOffice = this.serviceOffices.find(s => s.id === id);
                    return this.$globalHelpers.humanize(serviceOffice?.label || '');
                }).join(', ');
            }
            if (reportData.selectedExporterTypeId) {
                exporterTypeText = reportData.filteredExporterTypes.find(x => x.id === reportData.selectedExporterTypeId).label;
            }
            if (reportData.selectedFactoryTypeId) {
                factoryTypeText = reportData.filteredFactoryTypes.find(x => x.Id === reportData.selectedFactoryTypeId).Label;
            }
            if (Array.isArray(reportData.selectedExporterTypeIds) && reportData.selectedExporterTypeIds.length > 0 && reportData.selectedExporterTypeIds.length < this.exporterTypes.length) {
                exporterTypesText = reportData.selectedExporterTypeIds.map(id => {
                    const exporterType = this.exporterTypes.find(s => s.id === id);
                    return this.$globalHelpers.humanize(exporterType?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedStatusIds) && reportData.selectedStatusIds.length > 0 && reportData.selectedStatusIds.length < this.statuses.length) {
                statusText = reportData.selectedStatusIds.map(id => {
                    const status = this.statuses.find(s => s.id === id);
                    return this.$globalHelpers.humanize(status?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedRoleIds) && reportData.selectedRoleIds.length > 0 && reportData.selectedRoleIds.length < this.roles.length) {
                rolesText = reportData.selectedRoleIds.map(id => {
                    const role = this.roles.find(s => s.id === id);
                    return this.$globalHelpers.humanize(role?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedDesignationIds) && reportData.selectedDesignationIds.length > 0 && reportData.selectedDesignationIds.length < this.designations.length) {
                designationText = reportData.selectedDesignationIds.map(id => {
                    const designation = this.designations.find(s => s.id === id);
                    return this.$globalHelpers.humanize(designation?.label || '');
                }).join(', ');
            }
            if (Array.isArray(reportData.selectedValidTillYears) && reportData.selectedValidTillYears.length > 0 && reportData.selectedValidTillYears.length < this.validTillYears.length) {
                yearsText = reportData.selectedValidTillYears.join(', ');
            }

            const chunkSize = 18;
            const container = document.createElement('div');

            // Recursively apply inline styles to element and its children
            function applyInlineStylesRecursively(el) {
                if (el.nodeType === Node.ELEMENT_NODE) {
                    // Check for <td> or <th>
                    if (el.tagName === 'TD' || el.tagName === 'TH') {
                        const text = el.textContent?.trim() || '';
                        // Set font size based on text length
                        if (text.length > 32) {
                          el.textContent = text.substring(0, 100) + '..';
                        }
                        el.style.fontSize = text.length > 23 ? '10px' : '12px';

                        // Always apply padding
                        el.style.paddingLeft = '3px';
                        el.style.paddingright = '3px';
                    } else {
                        // For non-TD elements, apply default font size
                        el.style.fontSize = '12px';
                    }

                    // Recurse through children
                    for (const child of el.children) {
                        applyInlineStylesRecursively(child);
                    }
                }
            }

            // Create chunks of dataRows. If there are no data rows we still want to produce
            // a single table that contains the header and footer (if any).
            const chunks = [];
            if (dataRows.length === 0) {
                chunks.push([]);
            } else {
                for (let i = 0; i < dataRows.length; i += chunkSize) {
                    chunks.push(dataRows.slice(i, i + chunkSize));
                }
            }

            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                const chunk = chunks[chunkIndex];
                const table = document.createElement('table');
                table.setAttribute('border', '1');
                table.setAttribute('cellspacing', '0');
                table.style.width = '100%';
                table.style.marginBottom = '20px';
                table.style.pageBreakAfter = (chunkIndex < chunks.length - 1) ? 'always' : 'auto';

                const tbody = document.createElement('tbody');
                const columnCount = headerRow ? headerRow.querySelectorAll('td, th').length : 1;

                if (chunkIndex === 0) {
                    tbody.innerHTML += `
                        <tr style="border-color: #ffffff; text-align: center;">
                            <td style="border-color: #ffffff; text-align: center; padding-top: 5px;" colspan="${columnCount}">
                                <div style="width: 50%; margin: 0 auto;">
                                    <div style="float: left;">
                                        <img src="/images/logo.png" height="40" alt="Logo">
                                    </div>
                                    <div style="float: right;">
                                        <img src="/images/epbLogo.jpg" height="40" alt="EPB Logo">
                                    </div>
                                    <div style="text-align: center; font-size: 14px">
                                        <div style="font-size: 14px;">Export Promotion Bureau</div>
                                        <div>TCB Building, 1 Kawran Bazar, Dhaka</div>
                                    </div>
                                    <div style="clear: both;"></div>
                                </div>
                            </td>
                        </tr>
                        <tr><td colspan="${columnCount}" style="border-color: #ffffff; text-align: center;">Report: &nbsp; <strong>${config.title}</strong></td></tr>
                        <tr><td colspan="${columnCount}" style="border-color: #ffffff; text-align: center;">From: ${fromDate} To: ${toDate}</td></tr>
                        <tr>
                            <td colspan="${columnCount}" style="border-top-color: #ffffff; border-left-color: #ffffff; border-right-color: #ffffff; text-align: center; padding-bottom: 5px;">
                                ${config.reportKey === 'applications'? 'Type: ' + applicationTypeText + ', Department: ' + exporterTypeText + ', Factory Type: ' + factoryTypeText  + ', Service Office: ' + serviceOfficeText + ', Statuses: ' + statusText : ''}
                                ${config.reportKey === 'payments'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText: ''}
                                ${config.reportKey === 'users'? 'Type: ' + userTypeText + ', Department: ' + exporterTypesText + ', Service Office: ' + serviceOfficeText + ', Roles: ' + rolesText + ', Designations: ' + designationText : ''}
                                ${config.reportKey === 'validity'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText + ', Year: ' + yearsText : ''}
                                ${config.reportKey === 'inspections'? 'Application Type: ' + applicationTypeText + ', Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText : ''}
                                ${config.reportKey === 'inspectionCounts'? 'Service Office: ' + serviceOfficeText + ', Department: ' + exporterTypesText + ', Designations: ' + designationText : ''}
                            </td>
                        </tr>`;
                }

                const headerClone = headerRow ? headerRow.cloneNode(true) : null;
                applyInlineStylesRecursively(headerClone);
                if (headerClone) tbody.appendChild(headerClone);

                chunk.forEach(row => {
                    const rowClone = row.cloneNode(true);
                    applyInlineStylesRecursively(rowClone);
                    tbody.appendChild(rowClone);
                });

                // Append footer rows on the last chunk (or when there were no data rows)
                if (chunkIndex === chunks.length - 1) {
                    footerRows.forEach(row => {
                        const rowClone = row.cloneNode(true);
                        applyInlineStylesRecursively(rowClone);
                        tbody.appendChild(rowClone);
                    });
                }

                table.appendChild(tbody);
                container.appendChild(table);
            }
            const note = document.createElement('div');
            note.style.marginTop = '10px';
            note.style.fontStyle = 'italic';
            note.style.fontSize = '12px';
            note.style.textAlign = 'center';
            note.textContent = 'Promoting Export, Building Bangladesh';
            container.appendChild(note);

            //document.getElementById('debugReport').append(container);

            const opt = {
                margin: 0.4,
                filename: config.filenamePDF,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 1 },
                jsPDF: { unit: 'in', format: 'a4', orientation: config.reportKey === 'payments'? 'portrait': 'landscape' }
            };

            html2pdf().set(opt).from(container).save().then(() => {
                this.loading = false; // Hide spinner or update UI
            }).catch(err => {
                    this.loading = false;
                    console.log(err);
                });
        },
        getItems: function(itemsKey) {
            // Handle nested keys like 'reports.inspections.filteredExporterTypes'
            return itemsKey.split('.').reduce((obj, key) => obj && obj[key], this);
        },
        getNestedValue: function(obj, path) {
            return path.split('.').reduce((o, k) => o?.[k], obj);
        },
        getGenerateDisabled: function() {
            if (this.loading) return true;
            if (this.reportType === 'users' && this.reports.users.type === null) return true;
            const config = this.reportConfigs[this.reportType];
            if (config && config.filters.some(f => f.model === 'startDate' || f.model === 'endDate')) {
                if (!this.reports[this.reportType].startDate || !this.reports[this.reportType].endDate) return true;
            }
            return false;
        },
    },
    async created() {
        this.reports.applications.startDate = this.reports.payments.startDate = this.reports.inspections.startDate = this.reports.inspectionCounts.startDate = this.$globalHelpers.getDateToday('YYYY-MM-DD');
        this.reports.applications.endDate = this.reports.payments.endDate = this.reports.inspections.endDate = this.reports.inspectionCounts.endDate = this.$globalHelpers.getDateToday('YYYY-MM-DD');
        this.reports.applications.factoryDistricts = [...this.$globalSettings.districts].sort((a, b) => a.label.localeCompare(b.label));
        this.reports.users.factoryDistricts = this.reports.applications.factoryDistricts;

        this.applicationTypes = this.$globalHelpers.enumToArray(this.$globalEnums.ApplicationTypes);
        this.statuses = this.$globalHelpers.enumToArray(this.$globalEnums.ApplicationStatuses);
        this.serviceOffices = this.$globalHelpers.enumToArray(this.$globalEnums.ServiceOffices);
        this.roles = this.$globalHelpers.enumToArray(this.$globalEnums.roles);
        this.exporterTypes = this.$globalHelpers.enumToArray(this.$globalEnums.ExporterTypes);

        this.designations = this.$globalHelpers.enumToArray(this.$globalEnums.Designations);

        //Applications Report
        this.reports.applications.filteredExporterTypes = this.exporterTypes.filter(function(x){
            return x.id === 1 || x.id === 2;
        });

        //Validity Report
        this.reports.validity.filteredExporterTypes = this.exporterTypes.filter(function(x){
            return x.id === 1 || x.id === 2;
        });
        this.reports.applications.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });

        //Payments Report
        this.reports.payments.filteredExporterTypes = this.exporterTypes.filter(function(x){
            return x.id === 1 || x.id === 2;
        });
        this.reports.payments.selectedExporterTypeIds.push(this.$globalEnums.ExporterTypes.Textile);
        this.reports.payments.selectedExporterTypeIds.push(this.$globalEnums.ExporterTypes.NonTextile);
        this.reports.payments.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });

        //Users Report
        this.reports.users.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });
        this.reports.users.filteredDesignations = this.designations.filter(function(x){
            return x.id >= 1 && x.id < 100;
        });

        //Validity Report
        this.reports.validity.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });

        //Inspections Report
        this.reports.inspections.filteredExporterTypes = this.exporterTypes.filter(function(x){
            return x.id === 1 || x.id === 2;
        });
        this.reports.inspections.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });

        //InspectionCounts Report
        this.reports.inspectionCounts.filteredExporterTypes = this.exporterTypes;
        this.reports.inspectionCounts.filteredDesignations = this.designations.filter(function(x){
            return x.id >= 1 && x.id < 100;
        });
        this.reports.inspectionCounts.filteredServiceOffices = this.serviceOffices.filter(function(x){
            return x.id !== 0;
        });


        this.reportType = this.$route.params.type;
        this.showReport = false;

        if(this.reportType === 'applications'){
            await this.loadFactoryTypes();
        }

        this.$watch(
            () => this.$route.params,
            async (toParams) => {
                this.routeParams = toParams;
                this.reportType = this.routeParams.type;
                this.showReport = false;

                if(this.reportType === 'applications'){
                    await this.loadFactoryTypes();
                }
            }
        );
    }
}
</script>

<style scoped>

</style>